const getApiBaseUrl = () =>
  (typeof window !== 'undefined' && (window as any).ENV?.EXPO_PUBLIC_OPERATION_API_URL) || '';

export interface OperationItem {
  id: string;
  reference_number: string;
  type: 'echeque' | 'transferencia';
  status: 'pendiente' | 'aprobada' | 'rechazada';
  amount: number;
  due_date: string | null;
  description: string | null;
  rejection_reason: string | null;
  user_id: string;
  producer_name: string;
  producer_number: string;
  managed_by: string | null;
  managed_by_name: string | null;
  request_date: string;
  execution_date: string | null;
  created_at: string;
  updated_at: string | null;
}

export interface OperationsResponse {
  items: OperationItem[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface DashboardStats {
  pendientes: number;
  aprobadas: number;
  rechazadas: number;
  total: number;
  volumen_operado: number;
  del_dia: number;
  diferidas: number;
  cutoff_hora_arg: string;
}

export async function fetchOperations(
  token: string,
  params: {
    page?: number;
    limit?: number;
    status?: 'pendiente' | 'aprobada' | 'rechazada';
    type?: 'echeque' | 'transferencia';
    user_id?: string;
    search?: string;
    group?: 'del_dia' | 'diferidas';
    order?: 'asc' | 'desc';
  } = {}
): Promise<OperationsResponse> {
  let url = `${getApiBaseUrl()}/operation/all`;
  const urlParams = new URLSearchParams();
  
  if (params.page !== undefined) urlParams.append("page", String(params.page));
  if (params.limit !== undefined) urlParams.append("limit", String(params.limit));
  if (params.status) urlParams.append("status", params.status);
  if (params.type) urlParams.append("type", params.type);
  if (params.user_id) urlParams.append("user_id", params.user_id);
  if (params.search && params.search.trim() !== "") urlParams.append("search", params.search);
  if (params.group) urlParams.append("group", params.group);
  if (params.order) urlParams.append("order", params.order);

  const queryStr = urlParams.toString();
  if (queryStr) {
    url += "?" + queryStr;
  }

  const response = await fetch(url, {
    headers: {
      "Authorization": `Bearer ${token}`
    }
  });

  if (!response.ok) {
    throw new Error("Error al obtener las operaciones.");
  }

  const result = await response.json();
  return result.data;
}

export async function fetchStats(
  token: string,
  type?: 'echeque' | 'transferencia'
): Promise<DashboardStats> {
  let url = `${getApiBaseUrl()}/operation/stats`;
  if (type) {
    url += `?type=${type}`;
  }

  const response = await fetch(url, {
    headers: {
      "Authorization": `Bearer ${token}`
    }
  });

  if (!response.ok) {
    throw new Error("Error al obtener las estadísticas del dashboard.");
  }

  const result = await response.json();
  return result.data;
}

export async function approveOperation(
  operationId: string,
  token: string
): Promise<any> {
  const response = await fetch(`${getApiBaseUrl()}/operation/${operationId}/approve`, {
    method: "POST",
    headers: {
      "Authorization": `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Error al aprobar la operación.");
  }

  return response.json();
}

export async function rejectOperation(
  operationId: string,
  rejectionReason: string,
  token: string
): Promise<any> {
  const response = await fetch(`${getApiBaseUrl()}/operation/${operationId}/reject`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`
    },
    body: JSON.stringify({ rejection_reason: rejectionReason })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Error al rechazar la operación.");
  }

  return response.json();
}
